import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

/**
 * Multi-theme system — 4 luxury palettes.
 * Each theme defines CSS variables consumed by index.css utilities (gold-text, btn-gold, glass-gold).
 * Variable names stay the same (--gold, --gold-light, --gold-dark, --bg, etc.) so existing components stay theme-aware.
 */
export const THEMES = [
  {
    key: "gold",
    ar: "ذهبي ملكي",
    en: "Royal Gold",
    swatches: ["#0A0A0A", "#D4AF37", "#F9E596"],
    vars: {
      "--bg": "#0A0A0A",
      "--bg-2": "#111111",
      "--bg-3": "#1A1A1A",
      "--gold-light": "#F9E596",
      "--gold": "#D4AF37",
      "--gold-dark": "#AA8000",
      "--text": "#FFFFFF",
      "--text-dim": "#A1A1AA",
    },
  },
  {
    key: "royal",
    ar: "أزرق ملكي",
    en: "Royal Blue",
    swatches: ["#0B1F3A", "#D9C77B", "#F5EFE0"],
    vars: {
      "--bg": "#06122A",
      "--bg-2": "#0B1F3A",
      "--bg-3": "#13294B",
      "--gold-light": "#EFE2A7",
      "--gold": "#D9C77B",
      "--gold-dark": "#A99547",
      "--text": "#F5EFE0",
      "--text-dim": "#B5BCC8",
    },
  },
  {
    key: "emerald",
    ar: "زمردي فاخر",
    en: "Emerald Luxe",
    swatches: ["#0A1F1A", "#C9A561", "#EFE6CB"],
    vars: {
      "--bg": "#06140F",
      "--bg-2": "#0A1F1A",
      "--bg-3": "#103027",
      "--gold-light": "#EAD495",
      "--gold": "#C9A561",
      "--gold-dark": "#937535",
      "--text": "#EFE6CB",
      "--text-dim": "#B0BFB6",
    },
  },
  {
    key: "obsidian",
    ar: "بلاتيني داكن",
    en: "Obsidian Platinum",
    swatches: ["#0A0A0F", "#C9CFD6", "#E8C26F"],
    vars: {
      "--bg": "#06060A",
      "--bg-2": "#0A0A0F",
      "--bg-3": "#15151D",
      "--gold-light": "#F2DA9A",
      "--gold": "#E8C26F",
      "--gold-dark": "#A8884A",
      "--text": "#FFFFFF",
      "--text-dim": "#9CA3AF",
    },
  },
];

const ThemeContext = createContext(null);

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    if (typeof window === "undefined") return "gold";
    return window.localStorage.getItem("thqah_theme") || "gold";
  });

  useEffect(() => {
    const t = THEMES.find((x) => x.key === theme) || THEMES[0];
    const root = document.documentElement;
    Object.entries(t.vars).forEach(([k, v]) => root.style.setProperty(k, v));
    try { window.localStorage.setItem("thqah_theme", theme); } catch (_) {}
  }, [theme]);

  const cycle = useCallback(() => {
    setTheme((curr) => {
      const idx = THEMES.findIndex((t) => t.key === curr);
      return THEMES[(idx + 1) % THEMES.length].key;
    });
  }, []);

  const value = useMemo(() => ({ theme, setTheme, cycle, THEMES }), [theme, cycle]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => useContext(ThemeContext);
